import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Mail, Send, Clock, Check, X, RefreshCw } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const STATUS_COLORS = {
    pending: "bg-amber-100 text-amber-700",
    unlocked: "bg-green-100 text-green-700",
    declined: "bg-red-100 text-red-700",
    expired: "bg-gray-100 text-gray-600",
    accepted: "bg-green-100 text-green-700",
};

/**
 * Doctor-side panel: ask to see a patient's medical file by email.
 * The patient receives an OTP via email; once they enter it, the doctor's view of
 * the patient unlocks and a consultation is opened automatically.
 */
export function DoctorRequestAccessPanel({ onConsultationUnlocked }) {
    const { t } = useTranslation();
    const [email, setEmail] = useState("");
    const [reason, setReason] = useState("Consultation");
    const [sending, setSending] = useState(false);
    const [requests, setRequests] = useState([]);

    const load = async () => {
        try {
            const { data } = await api.get("/access-requests/mine");
            setRequests(data || []);
            // any newly-unlocked request → notify parent so it can open the consultation
            const unlocked = (data || []).find((r) => r.status === "unlocked" && r.unlocked_consultation_id);
            if (unlocked && onConsultationUnlocked) {
                onConsultationUnlocked(unlocked.unlocked_consultation_id);
            }
        } catch {/* silent */}
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 5000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!email) return;
        setSending(true);
        try {
            await api.post("/access-requests", { patient_email: email.trim(), reason });
            toast.success(t("consult.request.sent"));
            setEmail("");
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not send request");
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="card-soft p-6" data-testid="doctor-request-access-panel">
            <div className="flex items-center gap-2 mb-1">
                <div className="w-9 h-9 rounded-xl bg-orange-50 grid place-items-center text-[var(--medx-red)]">
                    <Mail className="w-5 h-5" />
                </div>
                <div>
                    <h3 className="font-display text-lg font-semibold">{t("consult.request.title")}</h3>
                    <p className="text-xs text-gray-500">{t("consult.request.body")}</p>
                </div>
            </div>

            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                <div className="md:col-span-1">
                    <label className="block text-xs font-mono tracking-wider text-gray-500 mb-1">
                        {t("consult.request.emailLabel").toUpperCase()}
                    </label>
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="patient@email.com"
                        required
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                        data-testid="dr-req-email"
                    />
                </div>
                <div className="md:col-span-1">
                    <label className="block text-xs font-mono tracking-wider text-gray-500 mb-1">
                        {t("consult.request.reasonLabel").toUpperCase()}
                    </label>
                    <input
                        type="text"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Consultation"
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                        data-testid="dr-req-reason"
                    />
                </div>
                <div className="md:col-span-1 flex items-end">
                    <button type="submit" disabled={sending} className="btn-primary w-full inline-flex items-center justify-center gap-2 !py-2.5 disabled:opacity-60" data-testid="dr-req-submit">
                        {sending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        {t("consult.request.send")}
                    </button>
                </div>
            </form>

            {requests.length > 0 && (
                <div className="mt-5">
                    <div className="text-xs font-mono tracking-wider text-gray-500 mb-2">RECENT REQUESTS</div>
                    <div className="space-y-2">
                        {requests.slice(0, 6).map((r) => (
                            <div key={r.id} className="flex items-center justify-between text-sm bg-gray-50 border border-gray-100 rounded-xl px-4 py-2.5" data-testid={`dr-req-row-${r.id}`}>
                                <div className="min-w-0">
                                    <div className="font-medium truncate">{r.patient_email}</div>
                                    <div className="text-xs text-gray-500 truncate">{r.reason}</div>
                                </div>
                                <div className="flex items-center gap-3 shrink-0">
                                    <span className={`text-[10px] font-mono px-2 py-1 rounded-full ${STATUS_COLORS[r.status] || "bg-gray-100"}`}>
                                        {r.status === "unlocked" ? <Check className="w-3 h-3 inline" /> : r.status === "declined" ? <X className="w-3 h-3 inline" /> : <Clock className="w-3 h-3 inline" />}
                                        {" "}{r.status}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
